import { Flex, Heading, Surface } from '@pinchenterprisesnpm/friday-ui'
import { DEFAULT_SURFACE_CURVATURE } from '@src/helpers/themeHelpers'
import { PropsWithChildren } from 'react'
import styled, { css } from 'styled-components'

/** Primary page heading. Place inside `PageHeader`. */
export const PageTitle = ({ children }: PropsWithChildren) => (
  <Heading headingType="h2" color="primary" m="0">
    {children}
  </Heading>
)

/**
 * Root shell for business pages.
 *
 * `$noPadding` should be avoided on new pages - use the standard shell instead.
 * Only use `$noPadding` on legacy pages that don't follow the standard shell - today that
 * means Full Edit Shift, Full Duplicate Shift, and Add/Edit Template.
 */
export const Page = styled(Flex).attrs({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
})<{ $noPadding?: boolean }>`
  min-height: 0;
  ${({ $noPadding }) =>
    $noPadding &&
    css`
      padding: 0;
    `}
  ${({ $noPadding }) =>
    !$noPadding &&
    css`
      padding-right: 15px;
      padding-left: 15px;
      margin-right: auto;
      margin-left: auto;
    `}
`

/**
 * Fixed chrome above PageContent — title, GoBack, and page-level actions. Never scrolls.
 *
 * Use `$mt` on most pages for top spacing;
 *
 * `$mr` / `$ml` — side inset (15px). Only needed when `Page $noPadding` removes the
 * standard horizontal padding (legacy Full Edit/Duplicate Shift, Add/Edit Template).
 */
export const PageHeader = styled.div<{
  $mr?: boolean
  $mb?: boolean
  $ml?: boolean
  $mt?: boolean
  justifyContent?: string
}>`
  flex-shrink: 0;
  height: 32px;
  display: flex;
  justify-content: ${({ justifyContent }) => justifyContent || 'space-between'};
  align-items: center;
  margin-bottom: ${({ $mb, theme: { mp } }) => $mb && mp.sm};
  margin-top: ${({ $mt, theme: { mp } }) => $mt && mp.sm};
  margin-right: ${({ $mr }) => $mr && '15px'};
  margin-left: ${({ $ml }) => $ml && '15px'};
`

/**
 * Main body below PageHeader. Fills remaining page height; stacks surfaces, forms, or
 * other blocks.
 *
 * Default — `overflow-y: hidden`; scroll is owned by a child (e.g. `PageSurface $fill
 * $scroll`, `PageSurface $fill $tableScroll`, or `FormExtended` → `ContainerExtended`).
 *
 * `$scroll` — PageContent scrolls. Use when content stacks at natural height with no
 * inner scroll region.
 */
export const PageContent = styled.div<{
  $scroll?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: ${({ theme: { mp } }) => mp.sm};
  padding-top: ${({ theme: { mp } }) => mp.sm};
  padding-bottom: ${({ theme: { mp } }) => mp.sm};
  overflow-x: hidden;
  overflow-y: ${({ $scroll }) => ($scroll ? 'auto' : 'hidden')};
`

/**
 * White card surface inside PageContent.
 *
 * | Mode | When to use |
 * |------|-------------|
 * | default | Natural-height block; use with `PageContent $scroll` when stacking multiple surfaces. |
 * | `$fill` | Single surface filling the page — forms (`ContainerExtended` owns scroll; do not add `$scroll`). |
 * | `$fill $scroll` | One scrollable block — filters/headers pinned, **last direct child scrolls**. |
 * | `$fill $tableScroll` | Pinned chrome + table + optional footer — wrap table in `PageSurfaceTable`. |
 */
export const PageSurface = styled(Surface).attrs({
  curvature: DEFAULT_SURFACE_CURVATURE,
})<{ $fill?: boolean; $scroll?: boolean; $tableScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      overflow: hidden;
    `}
  ${({ $fill, $tableScroll }) =>
    $fill &&
    $tableScroll &&
    css`
      & > *:not([data-page-surface-table]) {
        flex-shrink: 0;
      }

      & > *:has([data-page-surface-table]) {
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      & [data-page-surface-table] {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        min-width: 0;
        overflow-y: auto;
      }
    `}
  ${({ $fill, $scroll, $tableScroll }) =>
    $fill &&
    $scroll &&
    !$tableScroll &&
    css`
      & > *:not(:last-child) {
        flex-shrink: 0;
      }

      & > *:last-child {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        min-width: 0;
        overflow-y: auto;
      }
    `}
`

/** Scrollable table region inside `PageSurface $fill $tableScroll`. */
export const PageSurfaceTable = styled.div.attrs({
  'data-page-surface-table': '',
})`
  min-width: 0;
`
